import apaleoApi from 'api/apaleoApi';
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRY_KEY,
} from 'utils/apaleoAuth';

interface SelectedRatePlan {
  id: number;
  code: string;
  name: string;
}

interface SelectedProperty {
  id: number;
  name: string;
  code: string;
  ratePlans: SelectedRatePlan[];
}

type PropertySelectionRequest = SelectedProperty[];

interface PropertySelectionResponse {
  success: boolean;
  message?: string;
}

export interface RatePlanDto {
  id: number;
  propertyId?: number;
  name: string;
  code: string;
  shortDescription?: string;
  detailDescription?: string;
  policyCode?: string;
  policyName?: string;
  crsPolicyCode?: string;
  isRefundable?: boolean;
  currencyCode?: string;
  hotelRateCode?: string;
  isActive?: boolean;
  [key: string]: any;
}

export interface Property {
  id: number;
  name: string;
  code: string;
  hotelId?: number;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip?: string | null;
  address1?: string | null;
  timezoneId?: string;
  isActive?: boolean;
  ratePlans?: RatePlanDto[];
  [key: string]: any;
}

export interface PropertiesApiResponse {
  data: Property[];
  success: boolean;
  message: string;
}

export interface PropertyFilterRequest {
  filters: Array<{
    fieldName: string;
    fieldValues: string[];
  }>;
  page?: {
    size: number;
    number?: number;
  };
}

export interface RatePlanResponseDto {
  data: RatePlanDto[];
  success: boolean;
  message: string;
}

const apaleoApiSlice = apaleoApi.injectEndpoints({
  endpoints: (builder) => ({
    getProperties: builder.query<PropertiesApiResponse, void | undefined>({
      query: () => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/offboard/properties?token=${encodeURIComponent(token)}`,
          method: 'GET',
        };
      },
      providesTags: ['Properties'],
    }),
    exchangeCodeForToken: builder.mutation({
      query: ({ code, state }: { code: string; state: string }) => ({
        url: '/api/apaleo/token-exchange',
        method: 'POST',
        data: { code, state },
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data?.accessToken) {
            localStorage.setItem(TOKEN_KEY, data.accessToken);

            if (data.refresh_token) {
              localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
            }

            if (data.expiresIn) {
              localStorage.setItem(
                TOKEN_EXPIRY_KEY,
                (Date.now() + data.expiresIn * 1000).toString()
              );
            }
          }
        } catch (error) {
          console.error('Token exchange failed:', error);
          // Don't re-throw - let the component handle the error
        }
      },
      invalidatesTags: ['Token'],
    }),
    submitSelectedProperties: builder.mutation<
      PropertySelectionResponse,
      PropertySelectionRequest
    >({
      query: (selectedProperties) => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/metadata/update-property-with-rateplan?token=${encodeURIComponent(token)}`,
          method: 'POST',
          data: selectedProperties,
        };
      },
      invalidatesTags: ['Properties'],
    }),
    getRatePlans: builder.query<RatePlanResponseDto, { propertyId: string }>({
      query: ({ propertyId }) => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/metadata/rateplans?propertyId=${encodeURIComponent(propertyId)}&token=${encodeURIComponent(token)}`,
          method: 'GET',
        };
      },
      providesTags: (result, _error, { propertyId }) =>
        result?.success ? [{ type: 'RatePlan', id: propertyId }] : [],
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useExchangeCodeForTokenMutation,
  useSubmitSelectedPropertiesMutation,
  useGetRatePlansQuery,
} = apaleoApiSlice;
